import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Phone, 
  Mail, 
  Globe, 
  Building2, 
  Printer,
  Download,
  ArrowLeft,
  CheckCircle,
  ExternalLink,
  Loader2,
  Shield,
  MapPin
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PortalLayout from '../components/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default guidance that always works
const DEFAULT_GUIDANCE = {
  state_name: 'Your State',
  regulator_name: 'Your State Insurance Department',
  regulator_phone: 'Contact your state insurance department',
  regulator_website: 'https://content.naic.org/state-insurance-departments',
  regulator_online_complaint_url: 'https://content.naic.org/state-insurance-departments'
};

export default function ComplaintNoticePage() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [policyNumber, setPolicyNumber] = useState(null);
  const [guidance, setGuidance] = useState(DEFAULT_GUIDANCE);

  useEffect(() => {
    const loadData = async () => {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        // No token - still show page with default content
        setLoading(false);
        return;
      }

      try {
        // Try to get policy info
        const policyRes = await fetch(`${API_URL}/api/portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (policyRes.ok) {
          const data = await policyRes.json();
          const policyNum = data?.primary_policy?.policy_number;
          const state = data?.primary_policy?.controlling_state || '';
          
          setPolicyNumber(policyNum);

          // Try to get state-specific guidance
          if (state) {
            try {
              const guidanceRes = await fetch(`${API_URL}/api/complaint/compact/${state}`);
              if (guidanceRes.ok) {
                const guidanceData = await guidanceRes.json();
                setGuidance({
                  state_name: guidanceData.state_name || DEFAULT_GUIDANCE.state_name,
                  regulator_name: guidanceData.regulator_name || DEFAULT_GUIDANCE.regulator_name,
                  regulator_phone: guidanceData.regulator_phone || DEFAULT_GUIDANCE.regulator_phone,
                  regulator_website: guidanceData.regulator_website || DEFAULT_GUIDANCE.regulator_website,
                  regulator_online_complaint_url: guidanceData.regulator_online_complaint_url || DEFAULT_GUIDANCE.regulator_online_complaint_url
                });
              }
            } catch (e) {
              console.log('Could not fetch state guidance');
            }
          }
        }
      } catch (err) {
        console.log('Could not fetch policy data');
      }
      
      setLoading(false);
    };

    // Small delay to ensure localStorage is synced
    setTimeout(loadData, 100);
  }, []);

  const handleOpenPDF = () => {
    if (policyNumber) {
      window.open(`${API_URL}/api/policy/${policyNumber}/document/complaint_notice`, '_blank');
    }
  };

  const handlePrintPDF = () => {
    if (policyNumber) {
      const printWindow = window.open(`${API_URL}/api/policy/${policyNumber}/document/complaint_notice`, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    }
  };

  if (loading) {
    return (
      <PortalLayout pageTitle="Complaint Notice">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#3B5BDB] mx-auto mb-4" />
            <p className="text-slate-600">Loading...</p>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout pageTitle="Complaint Notice">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-0 py-4 sm:py-6">
        
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#3B5BDB] rounded-xl flex items-center justify-center">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Notice of Complaint Procedure
              </h1>
              <p className="text-slate-600">{guidance.state_name} Policyholder Rights</p>
            </div>
          </div>
        </div>

        {/* Action Buttons - only show if we have a policy */}
        {policyNumber && (
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <Button
              onClick={handleOpenPDF}
              className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            <Button
              onClick={handlePrintPDF}
              variant="outline"
              className="rounded-xl"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Document
            </Button>
          </div>
        )}

        {/* Important Notice */}
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 mb-1">Your Rights as a Policyholder</p>
                <p className="text-sm text-blue-800">
                  You have the right to file a complaint at any time. This notice explains how to contact us 
                  and how to contact your state insurance department if you are not satisfied with our response.
                  <strong> Filing a complaint will not affect your policy or pending claims.</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Contact LossPay */}
        <Card className="mb-6 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#3B5BDB] rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                1
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-3">Contact LossPay First</h2>
                <p className="text-slate-600 mb-4">
                  We encourage you to contact us first. We are committed to resolving your concerns quickly and fairly.
                </p>
                
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-[#3B5BDB]" />
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <a href="tel:1-833-567-7729" className="font-semibold text-slate-900 hover:text-[#3B5BDB]">
                        1-833-LOSSPAY (1-833-567-7729)
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-[#3B5BDB]" />
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <a href="mailto:complaints@losspay.com" className="font-semibold text-slate-900 hover:text-[#3B5BDB]">
                        complaints@losspay.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#3B5BDB] mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Mail</p>
                      <p className="font-semibold text-slate-900">
                        LossPay<br />
                        16192 Coastal Highway<br />
                        Lewes, DE 19958
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <p className="text-sm text-green-800">
                      <strong>Response Time:</strong> We will acknowledge your complaint within 3 business days 
                      and provide a response within 15 business days.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Contact State Regulator */}
        <Card className="mb-6 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                2
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-3">Contact Your State Insurance Department</h2>
                <p className="text-slate-600 mb-4">
                  If you are not satisfied with our response, you may file a complaint with your state's insurance regulator.
                </p>
                
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="h-5 w-5 text-orange-600" />
                    <p className="font-bold text-orange-900">{guidance.regulator_name}</p>
                  </div>
                  
                  <div className="space-y-3">
                    {guidance.regulator_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-orange-600" />
                        <div>
                          <p className="text-sm text-orange-700">Phone</p>
                          <p className="font-semibold text-orange-900">{guidance.regulator_phone}</p>
                        </div>
                      </div>
                    )}
                    
                    {guidance.regulator_website && (
                      <div className="flex items-center gap-3">
                        <Globe className="h-4 w-4 text-orange-600" />
                        <div>
                          <p className="text-sm text-orange-700">Website</p>
                          <a 
                            href={guidance.regulator_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-semibold text-orange-900 hover:underline flex items-center gap-1"
                          >
                            Visit Website
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {guidance.regulator_online_complaint_url && (
                      <div className="mt-4">
                        <a
                          href={guidance.regulator_online_complaint_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          File a Complaint Online
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What to Include */}
        <Card className="mb-6 border-slate-200">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">What to Include in Your Complaint</h2>
            <ul className="space-y-2">
              {[
                'Your policy number',
                'Your contact information',
                'A clear description of the issue',
                'Dates of any relevant events',
                'Copies of relevant correspondence',
                'What resolution you are seeking'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Footer Notice */}
        <div className="text-center py-6 border-t border-slate-200">
          <p className="text-sm text-slate-500">
            This notice is provided in accordance with state insurance regulations.
          </p>
          {policyNumber && (
            <p className="text-sm text-slate-400 mt-1">
              Policy: {policyNumber}
            </p>
          )}
        </div>

        {/* Bottom Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pb-8">
          {policyNumber && (
            <>
              <Button
                onClick={handleOpenPDF}
                className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                className="rounded-xl"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Document
              </Button>
            </>
          )}
          <Button
            onClick={() => navigate('/help?category=complaints')}
            variant="outline"
            className="rounded-xl"
          >
            More Complaint FAQs
          </Button>
        </div>
      </div>
    </PortalLayout>
  );
}
